import React from 'react';
import { Entity, RELATION_OWNED_BY } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  EntityRefLinks,
  entityRouteParams,
  entityRouteRef,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { Box, Card, Chip, Typography, makeStyles } from '@material-ui/core';

/** The annotation that names the Teams channels of a service, `;`-separated. */
const TEAMS_CHANNELS_ANNOTATION = 'schema.dp.netrtl.com/teams-channels';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    transition: theme.transitions.create('box-shadow'),
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
    // What is a link of its own on the card sits above the link of the card.
    '& a:not($cardLink), & button': {
      position: 'relative',
      zIndex: 1,
    },
  },
  // The link to the page of the service, stretched over the whole card.
  cardLink: {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
    },
    '&:focus-visible': {
      outline: 'none',
    },
    '&:focus-visible::after': {
      outline: `2px solid ${theme.palette.primary.main}`,
      borderRadius: theme.shape.borderRadius,
    },
  },
  header: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold as number,
    lineHeight: 1.3,
  },
  deprecated: {
    opacity: 0.55,
    textDecoration: 'line-through',
  },
  system: {
    display: 'block',
    color: theme.palette.text.secondary,
  },
  content: {
    padding: theme.spacing(1, 1.5, 1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  description: {
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  label: {
    display: 'block',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    lineHeight: 1.6,
  },
  chip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
}));

/** A section of the card under a small label. */
const Section = (props: { label: string; children: React.ReactNode }) => {
  const classes = useStyles();
  return (
    <div>
      <Typography variant="caption" className={classes.label}>
        {props.label}
      </Typography>
      {props.children}
    </div>
  );
};

/** The Teams channels a service names, in the order it names them. */
export function teamsChannelsOf(service: Entity): string[] {
  return (service.metadata.annotations?.[TEAMS_CHANNELS_ANNOTATION] ?? '')
    .split(';')
    .map(channel => channel.trim())
    .filter(Boolean);
}

/**
 * Props for {@link ServiceCard}.
 *
 * @public
 */
export type ServiceCardProps = {
  entity: Entity;
  /** The title of the system the service belongs to, if it has one. */
  system?: string;
};

/**
 * A platform service at a glance: what it offers, who runs it, where to talk
 * about it, and where to read more.
 *
 * The whole card leads to the page of the service, the way a team card does:
 * the link of its title is stretched over it. What is a link of its own on it
 * - the owner, a link chip - sits above and goes where it says instead.
 *
 * @public
 */
export const ServiceCard = (props: Readonly<ServiceCardProps>) => {
  const { entity, system } = props;
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const title = entity.metadata.title ?? entity.metadata.name;
  const lifecycle = entity.spec?.lifecycle as string | undefined;
  const owners = getEntityRelations(entity, RELATION_OWNED_BY);
  const channels = teamsChannelsOf(entity);
  const links = entity.metadata.links ?? [];

  return (
    <Card className={classes.card}>
      <div className={classes.header}>
        {system && (
          <Typography variant="caption" className={classes.system}>
            {system}
          </Typography>
        )}
        <Typography
          variant="h6"
          component="h3"
          className={`${classes.title} ${
            lifecycle === 'deprecated' ? classes.deprecated : ''
          }`}
        >
          <Link
            to={entityRoute(entityRouteParams(entity))}
            color="inherit"
            underline="none"
            className={classes.cardLink}
          >
            {title}
          </Link>
        </Typography>
      </div>
      <div className={classes.content}>
        {entity.metadata.description && (
          <Typography variant="body2" className={classes.description}>
            {entity.metadata.description}
          </Typography>
        )}
        {lifecycle && lifecycle !== 'production' && (
          <Box>
            <Chip size="small" variant="outlined" label={lifecycle} />
          </Box>
        )}
        <Section label="Owner">
          {owners.length > 0 ? (
            <Typography variant="body2">
              <EntityRefLinks entityRefs={owners} defaultKind="group" />
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No owner yet
            </Typography>
          )}
        </Section>
        {channels.length > 0 && (
          <Section label="Teams channels">
            <Box display="flex" flexWrap="wrap">
              {channels.map(channel => (
                <Chip
                  key={channel}
                  className={classes.chip}
                  size="small"
                  label={channel}
                />
              ))}
            </Box>
          </Section>
        )}
        {links.length > 0 && (
          <Section label="Links">
            <Box display="flex" flexWrap="wrap">
              {links.map(link => (
                <Chip
                  key={link.url}
                  className={classes.chip}
                  size="small"
                  clickable
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  label={link.title ?? link.url}
                />
              ))}
            </Box>
          </Section>
        )}
      </div>
    </Card>
  );
};
